import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Users, Clock, LogIn, LogOut, ChevronDown, ChevronUp,
  Calendar, RefreshCw, CheckCircle2, AlertCircle, Timer,
  UserCheck, X, ClipboardList,
} from "lucide-react";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function getHeaders() {
  const token = localStorage.getItem("ai2_token");
  return { Authorization: `Bearer ${token}` };
}

// ── Types ────────────────────────────────────────────────────
interface AttendanceRecord {
  id: number;
  staff_name: string;
  staff_id: string;
  role: string;
  clock_in: string | null;
  clock_out: string | null;
  date: string;
  shift: "morning" | "afternoon" | "night";
  notes: string;
  late_minutes: number;
  overtime_minutes: number;
  created_at: string;
}

interface AttendanceStats {
  present: number;
  absent: number;
  late: number;
  onShift: number;
  totalStaff: number;
}

type ShiftFilter = "all" | "morning" | "afternoon" | "night";

// ── Constants ────────────────────────────────────────────────
const SHIFT_COLORS: Record<string, string> = {
  morning: "#f59e0b",
  afternoon: "#1a3a5c",
  night: "#1a3a5c",
};

const SHIFT_BG: Record<string, string> = {
  morning: "rgba(245,158,11,0.12)",
  afternoon: "rgba(201,168,76,0.12)",
  night: "rgba(201,168,76,0.12)",
};

const ROLE_OPTIONS = [
  "receptionist",
  "housekeeping",
  "restaurant",
  "security",
  "maintenance",
  "other",
];

const ROLE_COLORS: Record<string, { color: string; bg: string }> = {
  receptionist: { color: "#34d399", bg: "rgba(52,211,153,0.1)" },
  housekeeping: { color: "#fbbf24", bg: "rgba(251,191,36,0.1)" },
  restaurant:   { color: "#c9a84c", bg: "rgba(201,168,76,0.1)" },
  security:     { color: "#f87171", bg: "rgba(248,113,113,0.1)" },
  maintenance:  { color: "#fb923c", bg: "rgba(251,146,60,0.1)" },
  other:        { color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.1)" },
};

// ── Helpers ──────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().split("T")[0];
}

function formatTime(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function calcDuration(clockIn: string | null, clockOut: string | null): string {
  if (!clockIn) return "—";
  const start = new Date(clockIn).getTime();
  const end = clockOut ? new Date(clockOut).getTime() : Date.now();
  const diffMs = end - start;
  if (diffMs < 0) return "—";
  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${hours}h ${mins}m`;
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Sub-components ───────────────────────────────────────────

function ShiftBadge({ shift }: { shift: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 99,
        background: SHIFT_BG[shift] || "rgba(100,116,139,0.1)",
        color: SHIFT_COLORS[shift] || "#64748b",
        letterSpacing: 0.5,
        textTransform: "capitalize",
        border: `1px solid ${SHIFT_COLORS[shift] || "#64748b"}33`,
      }}
    >
      {shift}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const meta = ROLE_COLORS[role] || ROLE_COLORS.other;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 99,
        background: meta.bg,
        color: meta.color,
        letterSpacing: 0.3,
        textTransform: "capitalize",
      }}
    >
      {role}
    </span>
  );
}

// ── Clock-Out Modal ──────────────────────────────────────────
interface ClockOutModalProps {
  record: AttendanceRecord;
  onClose: () => void;
  onConfirm: (id: number, notes: string) => Promise<void>;
}

function ClockOutModal({ record, onClose, onConfirm }: ClockOutModalProps) {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleConfirm() {
    setLoading(true);
    setError("");
    try {
      await onConfirm(record.id, notes);
      onClose();
    } catch {
      setError("Failed to clock out. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#0E1E38",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#0E1E38",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: "32px 36px",
          width: 420,
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(248,113,113,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LogOut size={18} color="#f87171" />
            </div>
            <div>
              <div style={{ color: "#FDFAF4", fontWeight: 700, fontSize: 16 }}>Clock Out</div>
              <div style={{ color: "rgba(242,235,217,0.4)", fontSize: 12 }}>Confirm attendance end</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(100,116,139,0.1)",
              border: "none",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={16} color="#64748b" />
          </button>
        </div>

        {/* Staff info */}
        <div
          style={{
            background: "rgba(14,34,64,0.10)",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 20,
          }}
        >
          <div style={{ color: "#FDFAF4", fontWeight: 600, fontSize: 14 }}>{record.staff_name}</div>
          <div style={{ color: "rgba(242,235,217,0.4)", fontSize: 12, marginTop: 4 }}>
            {record.staff_id} · {record.role} · {record.shift} shift
          </div>
          <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12, marginTop: 6 }}>
            Clocked in at <strong style={{ color: "#C9A84C" }}>{formatTime(record.clock_in)}</strong>
            &nbsp;· Duration so far: <strong style={{ color: "#C9A84C" }}>{calcDuration(record.clock_in, null)}</strong>
          </div>
        </div>

        {/* Notes */}
        <label style={{ display: "block", marginBottom: 16 }}>
          <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Notes (optional)
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any remarks about this shift..."
            rows={3}
            style={{
              width: "100%",
              background: "#0E1E38",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "10px 12px",
              color: "#FDFAF4",
              fontSize: 13,
              resize: "vertical",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </label>

        {error && (
          <div
            style={{
              background: "rgba(248,113,113,0.1)",
              border: "1px solid rgba(248,113,113,0.2)",
              borderRadius: 8,
              padding: "10px 14px",
              color: "#f87171",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: 8,
              border: "1px solid rgba(100,116,139,0.2)",
              background: "transparent",
              color: "rgba(242,235,217,0.45)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: 8,
              border: "none",
              background: loading ? "rgba(248,113,113,0.3)" : "linear-gradient(135deg, #f87171, #ef4444)",
              color: "#0E1E38",
              fontSize: 14,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {loading ? (
              <RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} />
            ) : (
              <LogOut size={14} />
            )}
            {loading ? "Clocking Out..." : "Confirm Clock Out"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────
export default function StaffAttendancePage() {
  const [date, setDate] = useState(todayStr());
  const [shiftFilter, setShiftFilter] = useState<ShiftFilter>("all");
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");

  // Clock-in form
  const [showForm, setShowForm] = useState(false);
  const [formStaffName, setFormStaffName] = useState("");
  const [formStaffId, setFormStaffId] = useState("");
  const [formRole, setFormRole] = useState("receptionist");
  const [formShift, setFormShift] = useState<"morning" | "afternoon" | "night">("morning");
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // Clock-out modal
  const [clockOutRecord, setClockOutRecord] = useState<AttendanceRecord | null>(null);

  const headers = getHeaders();

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, string> = { date };
      if (shiftFilter !== "all") params.shift = shiftFilter;
      const res = await axios.get(`${API}/api/staff-attendance`, { params, headers });
      setRecords(Array.isArray(res.data) ? res.data : res.data.records || []);
    } catch {
      setError("Failed to load attendance records.");
    } finally {
      setLoading(false);
    }
  }, [date, shiftFilter]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/api/staff-attendance/stats`, {
        params: { date },
        headers,
      });
      setStats(res.data);
    } catch {
      // stats failure is non-blocking
    } finally {
      setStatsLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchRecords();
    fetchStats();
  }, [fetchRecords, fetchStats]);

  async function handleClockIn() {
    if (!formStaffName.trim() || !formStaffId.trim()) {
      setFormError("Staff name and ID are required.");
      return;
    }
    setFormLoading(true);
    setFormError("");
    setFormSuccess("");
    try {
      await axios.post(
        `${API}/api/staff-attendance/clockin`,
        {
          staff_name: formStaffName.trim(),
          staff_id: formStaffId.trim(),
          role: formRole,
          shift: formShift,
          date,
        },
        { headers }
      );
      setFormSuccess(`${formStaffName} clocked in successfully.`);
      setFormStaffName("");
      setFormStaffId("");
      fetchRecords();
      fetchStats();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || "Failed to clock in.");
    } finally {
      setFormLoading(false);
    }
  }

  async function handleClockOut(id: number, notes: string) {
    await axios.put(
      `${API}/api/staff-attendance/${id}/clockout`,
      { notes },
      { headers }
    );
    fetchRecords();
    fetchStats();
  }

  // ── Computed ─────────────────────────────────────────────
  const overtimeTotal = records.reduce((sum, r) => sum + (r.overtime_minutes || 0), 0);
  const overtimeHours = (overtimeTotal / 60).toFixed(1);

  // ── Styles (inline) ──────────────────────────────────────
  const s = {
    page: {
      minHeight: "100vh",
      background: "transparent",
      padding: "28px 32px",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    } as React.CSSProperties,

    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: 28,
      gap: 16,
      flexWrap: "wrap" as const,
    },

    title: {
      color: "#FDFAF4",
      fontSize: 24,
      fontWeight: 800,
      margin: 0,
      letterSpacing: -0.5,
    },

    subtitle: {
      color: "rgba(242,235,217,0.45)",
      fontSize: 13,
      marginTop: 4,
    },

    card: {
      background: "#0E1E38",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "20px 24px",
    } as React.CSSProperties,

    input: {
      background: "#0E1E38",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      padding: "9px 12px",
      color: "#FDFAF4",
      fontSize: 13,
      outline: "none",
      width: "100%",
      boxSizing: "border-box" as const,
    } as React.CSSProperties,

    select: {
      background: "#0E1E38",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      padding: "9px 12px",
      color: "#FDFAF4",
      fontSize: 13,
      outline: "none",
      cursor: "pointer",
    } as React.CSSProperties,

    btn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "9px 18px",
      borderRadius: 8,
      border: "none",
      fontWeight: 600,
      fontSize: 13,
      cursor: "pointer",
    } as React.CSSProperties,
  };

  return (
    <DashboardLayout>
      <div style={s.page}>
        {/* ── Page Header ── */}
        <div style={s.header}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "rgba(201,168,76,0.10)",
                  border: "1px solid rgba(201,168,76,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Users size={22} color="#1a3a5c" />
              </div>
              <div>
                <h1 style={s.title}>Staff Attendance</h1>
                <p style={s.subtitle}>Track check-ins, shifts and overtime</p>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* Date picker */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Calendar size={15} color="#1a3a5c" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  ...s.input,
                  width: "auto",
                  colorScheme: "light",
                  cursor: "pointer",
                }}
              />
            </div>

            <button
              onClick={() => { fetchRecords(); fetchStats(); }}
              style={{
                ...s.btn,
                background: "rgba(201,168,76,0.12)",
                color: "#C9A84C",
                border: "1px solid #e2e8f0",
              }}
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {/* ── Stats Strip ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 14,
            marginBottom: 24,
          }}
        >
          {[
            {
              label: "Present",
              value: statsLoading ? "—" : stats?.present ?? "—",
              icon: <CheckCircle2 size={18} color="#34d399" />,
              color: "#34d399",
              bg: "rgba(52,211,153,0.08)",
              borderColor: "rgba(52,211,153,0.15)",
            },
            {
              label: "On Shift",
              value: statsLoading ? "—" : stats?.onShift ?? "—",
              icon: <UserCheck size={18} color="#1a3a5c" />,
              color: "#C9A84C",
              bg: "rgba(14,34,64,0.10)",
              borderColor: "rgba(201,168,76,0.12)",
            },
            {
              label: "Late",
              value: statsLoading ? "—" : stats?.late ?? "—",
              icon: <AlertCircle size={18} color="#f87171" />,
              color: "#f87171",
              bg: "rgba(248,113,113,0.08)",
              borderColor: "rgba(248,113,113,0.15)",
            },
            {
              label: "Overtime hrs",
              value: statsLoading ? "—" : overtimeHours,
              icon: <Timer size={18} color="#f59e0b" />,
              color: "#f59e0b",
              bg: "rgba(245,158,11,0.08)",
              borderColor: "rgba(245,158,11,0.15)",
            },
            {
              label: "Total Records",
              value: statsLoading ? "—" : records.length,
              icon: <ClipboardList size={18} color="#c9a84c" />,
              color: "#c9a84c",
              bg: "rgba(201,168,76,0.08)",
              borderColor: "rgba(201,168,76,0.20)",
            },
          ].map((card) => (
            <div
              key={card.label}
              style={{
                background: card.bg,
                border: `1px solid ${card.borderColor}`,
                borderRadius: 14,
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: "rgba(242,235,217,0.4)", fontSize: 12, fontWeight: 600, letterSpacing: 0.3 }}>
                  {card.label.toUpperCase()}
                </span>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "rgba(201,168,76,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </div>
              </div>
              <div style={{ color: card.color, fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                {card.value}
              </div>
            </div>
          ))}
        </div>

        {/* ── Shift Filter Tabs ── */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 20,
            background: "#0E1E38",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: 6,
            width: "fit-content",
          }}
        >
          {(["all", "morning", "afternoon", "night"] as ShiftFilter[]).map((tab) => {
            const isActive = shiftFilter === tab;
            const color = tab === "all" ? "#1a3a5c" : SHIFT_COLORS[tab];
            return (
              <button
                key={tab}
                onClick={() => setShiftFilter(tab)}
                style={{
                  padding: "7px 18px",
                  borderRadius: 7,
                  border: "none",
                  background: isActive
                    ? tab === "all"
                      ? "rgba(14,34,64,0.10)"
                      : SHIFT_BG[tab]
                    : "transparent",
                  color: isActive ? color : "#475569",
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  textTransform: "capitalize",
                }}
              >
                {tab === "all" ? "All Shifts" : capitalize(tab)}
              </button>
            );
          })}
        </div>

        {/* ── Clock-In Form Panel ── */}
        <div style={{ ...s.card, marginBottom: 24 }}>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: "rgba(52,211,153,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LogIn size={16} color="#34d399" />
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ color: "#FDFAF4", fontWeight: 700, fontSize: 15 }}>Clock In Staff</div>
                <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12 }}>Record attendance for a team member</div>
              </div>
            </div>
            {showForm ? (
              <ChevronUp size={18} color="#1a3a5c" />
            ) : (
              <ChevronDown size={18} color="#1a3a5c" />
            )}
          </button>

          {showForm && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 20,
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: 14,
                  marginBottom: 16,
                }}
              >
                {/* Staff Name */}
                <label>
                  <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Staff Name *
                  </div>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={formStaffName}
                    onChange={(e) => setFormStaffName(e.target.value)}
                    style={s.input}
                  />
                </label>

                {/* Staff ID */}
                <label>
                  <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Staff ID *
                  </div>
                  <input
                    type="text"
                    placeholder="e.g. EMP-001"
                    value={formStaffId}
                    onChange={(e) => setFormStaffId(e.target.value)}
                    style={s.input}
                  />
                </label>

                {/* Role */}
                <label>
                  <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Role
                  </div>
                  <select
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value)}
                    style={{ ...s.select, width: "100%" }}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {capitalize(r)}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Shift */}
                <label>
                  <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Shift
                  </div>
                  <select
                    value={formShift}
                    onChange={(e) => setFormShift(e.target.value as typeof formShift)}
                    style={{ ...s.select, width: "100%" }}
                  >
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="night">Night</option>
                  </select>
                </label>
              </div>

              {formError && (
                <div
                  style={{
                    background: "rgba(248,113,113,0.08)",
                    border: "1px solid rgba(248,113,113,0.2)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    color: "#f87171",
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  {formError}
                </div>
              )}

              {formSuccess && (
                <div
                  style={{
                    background: "rgba(52,211,153,0.08)",
                    border: "1px solid rgba(52,211,153,0.2)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    color: "#34d399",
                    fontSize: 13,
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <CheckCircle2 size={14} />
                  {formSuccess}
                </div>
              )}

              <button
                onClick={handleClockIn}
                disabled={formLoading}
                style={{
                  ...s.btn,
                  background: formLoading
                    ? "rgba(52,211,153,0.2)"
                    : "linear-gradient(135deg, #34d399, #10b981)",
                  color: "#064e3b",
                  fontWeight: 700,
                  fontSize: 14,
                  padding: "10px 24px",
                  cursor: formLoading ? "not-allowed" : "pointer",
                  opacity: formLoading ? 0.7 : 1,
                }}
              >
                {formLoading ? (
                  <RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <LogIn size={14} />
                )}
                {formLoading ? "Clocking In..." : "Clock In"}
              </button>
            </div>
          )}
        </div>

        {/* ── Attendance Table ── */}
        <div style={s.card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Clock size={18} color="#1a3a5c" />
              <span style={{ color: "#FDFAF4", fontWeight: 700, fontSize: 16 }}>
                Attendance Records
              </span>
              <span
                style={{
                  background: "rgba(201,168,76,0.12)",
                  color: "#C9A84C",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 99,
                }}
              >
                {records.length}
              </span>
            </div>
          </div>

          {error && (
            <div
              style={{
                background: "rgba(248,113,113,0.08)",
                border: "1px solid rgba(248,113,113,0.2)",
                borderRadius: 8,
                padding: "12px 16px",
                color: "#f87171",
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "rgba(242,235,217,0.45)" }}>
              <RefreshCw
                size={24}
                color="#1a3a5c"
                style={{ animation: "spin 1s linear infinite", marginBottom: 12 }}
              />
              <div style={{ fontSize: 14 }}>Loading attendance records…</div>
            </div>
          ) : records.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "rgba(242,235,217,0.45)" }}>
              <Users size={40} color="rgba(14,34,64,0.10)" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(242,235,217,0.4)" }}>No records found</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                No attendance data for this date and shift.
              </div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    {[
                      "Staff Name",
                      "ID",
                      "Role",
                      "Shift",
                      "Clock In",
                      "Clock Out",
                      "Duration",
                      "Late",
                      "Overtime",
                      "Actions",
                    ].map((col) => (
                      <th
                        key={col}
                        style={{
                          textAlign: "left",
                          padding: "10px 14px",
                          color: "rgba(242,235,217,0.45)",
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: 0.5,
                          borderBottom: "1px solid #e2e8f0",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec, i) => (
                    <tr
                      key={rec.id}
                      style={{
                        background:
                          i % 2 === 0
                            ? "transparent"
                            : "rgba(14,34,64,0.10)",
                        borderBottom: "1px solid #e2e8f0",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLTableRowElement).style.background =
                          "rgba(14,34,64,0.10)")
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLTableRowElement).style.background =
                          i % 2 === 0 ? "transparent" : "rgba(14,34,64,0.10)")
                      }
                    >
                      {/* Staff Name */}
                      <td style={{ padding: "12px 14px", color: "#FDFAF4", fontWeight: 600 }}>
                        {rec.staff_name}
                      </td>

                      {/* ID */}
                      <td style={{ padding: "12px 14px", color: "rgba(242,235,217,0.4)", fontFamily: "monospace", fontSize: 12 }}>
                        {rec.staff_id}
                      </td>

                      {/* Role */}
                      <td style={{ padding: "12px 14px" }}>
                        <RoleBadge role={rec.role} />
                      </td>

                      {/* Shift */}
                      <td style={{ padding: "12px 14px" }}>
                        <ShiftBadge shift={rec.shift} />
                      </td>

                      {/* Clock In */}
                      <td style={{ padding: "12px 14px", color: "rgba(242,235,217,0.45)", whiteSpace: "nowrap" }}>
                        {rec.clock_in ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <LogIn size={12} color="#34d399" />
                            {formatTime(rec.clock_in)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      {/* Clock Out */}
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                        {rec.clock_out ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              color: "rgba(242,235,217,0.45)",
                            }}
                          >
                            <LogOut size={12} color="#f87171" />
                            {formatTime(rec.clock_out)}
                          </span>
                        ) : rec.clock_in ? (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "3px 9px",
                              borderRadius: 99,
                              background: "rgba(52,211,153,0.12)",
                              color: "#34d399",
                              border: "1px solid rgba(52,211,153,0.2)",
                              animation: "pulse 2s ease-in-out infinite",
                            }}
                          >
                            On Shift
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      {/* Duration */}
                      <td style={{ padding: "12px 14px", color: "rgba(242,235,217,0.45)", whiteSpace: "nowrap" }}>
                        {rec.clock_in && !rec.clock_out ? (
                          <span style={{ color: "#C9A84C", fontWeight: 600 }}>
                            {calcDuration(rec.clock_in, null)}
                            <span style={{ color: "rgba(242,235,217,0.45)", fontWeight: 400, fontSize: 11 }}>
                              {" "}(in progress)
                            </span>
                          </span>
                        ) : (
                          calcDuration(rec.clock_in, rec.clock_out)
                        )}
                      </td>

                      {/* Late */}
                      <td style={{ padding: "12px 14px" }}>
                        {rec.late_minutes > 0 ? (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "3px 9px",
                              borderRadius: 99,
                              background: "rgba(248,113,113,0.12)",
                              color: "#f87171",
                              border: "1px solid rgba(248,113,113,0.2)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rec.late_minutes} min late
                          </span>
                        ) : (
                          <span style={{ color: "#34d399", fontSize: 12 }}>On time</span>
                        )}
                      </td>

                      {/* Overtime */}
                      <td style={{ padding: "12px 14px" }}>
                        {rec.overtime_minutes > 0 ? (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "3px 9px",
                              borderRadius: 99,
                              background: "rgba(52,211,153,0.1)",
                              color: "#34d399",
                              border: "1px solid rgba(52,211,153,0.2)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            +{rec.overtime_minutes} min
                          </span>
                        ) : (
                          <span style={{ color: "rgba(242,235,217,0.45)", fontSize: 12 }}>—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "12px 14px" }}>
                        {rec.clock_in && !rec.clock_out ? (
                          <button
                            onClick={() => setClockOutRecord(rec)}
                            style={{
                              ...s.btn,
                              background: "rgba(248,113,113,0.1)",
                              color: "#f87171",
                              border: "1px solid rgba(248,113,113,0.2)",
                              fontSize: 12,
                              padding: "6px 14px",
                            }}
                          >
                            <LogOut size={12} />
                            Clock Out
                          </button>
                        ) : rec.clock_out ? (
                          <span style={{ color: "rgba(242,235,217,0.45)", fontSize: 12 }}>Completed</span>
                        ) : (
                          <span style={{ color: "rgba(242,235,217,0.45)", fontSize: 12 }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Clock-Out Modal ── */}
        {clockOutRecord && (
          <ClockOutModal
            record={clockOutRecord}
            onClose={() => setClockOutRecord(null)}
            onConfirm={handleClockOut}
          />
        )}

        {/* ── Keyframe styles ── */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.6; }
          }
          input[type="date"]::-webkit-calendar-picker-indicator {
            filter: invert(0.6) sepia(1) saturate(2) hue-rotate(200deg);
            cursor: pointer;
          }
          select option {
            background: #111927;
            color: #f1f5f9;
          }
        `}</style>
      </div>
    </DashboardLayout>
  );
}
